import {
  ChevronDownIcon,
  PlusIcon,
  User02Icon,
} from '@langchain/untitled-ui-icons';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CopyInlineLink } from '@/components/CopyInlineLink';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { ProfileCircle } from '@/components/ProfileCircle';
import { SAMLEmptyStateTooltip } from '@/components/SAMLEmptyStateTooltip';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Badge, Button, Text } from '@/design-system';
import { InstanceFlags, useInstanceFlag } from '@/hooks/useInstanceFlag';
import { usePermissions } from '@/hooks/usePermissions';
import { useSAMLEmptyState } from '@/hooks/useSAMLEmptyState';
import { useCurrentOrganization, useOrganizations } from '@/hooks/useSwr';
import CheckIcon from '@/icons/CheckIcon.svg?react';
import { OrganizationBaseSchema } from '@/types/schema';
import { appOrganizationPath, appSettingsPath } from '@/utils/constants';
import { isSelfHostedSSO } from '@/utils/is-self-hosted';
import { cn } from '@/utils/tailwind';

import { planToDisplayName } from '../utils/planToDisplayName';
import { CreateOrganizationModal } from './CreateTenantModal';
import { DefaultOrgModal } from './DefaultOrgModal';

const OrganizationIcon = (props: {
  org: { is_personal: boolean; display_name: string };
  className?: string;
}) => {
  const { org, className } = props;

  return org.is_personal ? (
    <ProfileCircle
      name={''}
      backupIcon={<User02Icon className="size-3 shrink-0 text-white" />}
      className={className}
    />
  ) : (
    <ProfileCircle name={org.display_name} className={className} />
  );
};

interface OrganizationButtonProps {
  org: OrganizationBaseSchema;
  orgTenants?: Array<{ id: string; organization_id: string | null }>;
  isSelected: boolean;
  isSAMLEmptyState: boolean;
  onSelect: () => void;
}

const OrganizationButton = ({
  org,
  isSelected,
  isSAMLEmptyState,
  onSelect,
}: OrganizationButtonProps) => {
  const { tier, is_personal: isPersonal } = org ?? {};
  const button = (
    <button
      type="button"
      className={cn(
        'flex w-full items-center justify-between gap-2 p-1.5 px-3 hover:bg-secondary',
        isSelected && 'bg-secondary',
        isSAMLEmptyState && 'cursor-not-allowed opacity-50'
      )}
      onClick={() => {
        if (isSAMLEmptyState) {
          // Prevent navigation for SAML users in empty state
          return;
        }
        onSelect();
      }}
    >
      <OrganizationIcon org={org} />
      <TextOverflowTooltip className="flex-1 text-left">
        {org.display_name}
      </TextOverflowTooltip>
      {isSelected && <CheckIcon className="h-4 w-4 shrink-0" />}
      {tier && (
        <Badge
          color={isPersonal ? 'special' : 'primary'}
          size="xs"
          rounded="xs"
        >
          {planToDisplayName(tier).toUpperCase()}
        </Badge>
      )}
    </button>
  );

  return isSAMLEmptyState ? (
    <SAMLEmptyStateTooltip key={org.id}>{button}</SAMLEmptyStateTooltip>
  ) : (
    button
  );
};

export const OrgPicker = (props: { className?: string }) => {
  const [open, setOpen] = useState(false);
  const { data: organizations } = useOrganizations({ includeTier: true });
  const currentOrg = useCurrentOrganization();
  const navigate = useNavigate();
  const { authorize } = usePermissions('organization');
  const orgCreationDisabled = useInstanceFlag(
    InstanceFlags.org_creation_disabled
  );

  const { isSAMLEmptyState, tenants } = useSAMLEmptyState();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('flex justify-between px-1 py-2')}>
        <Text weight="semibold">Organizations </Text>
        <CopyInlineLink className="font-normal" value={currentOrg.data?.id}>
          <Text>Org ID</Text>
        </CopyInlineLink>
      </div>
      <PopoverTrigger
        className={cn(
          'flex flex-1 items-center justify-between rounded-md border border-secondary hover:bg-secondary',
          'px-2 py-1',
          props.className
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {currentOrg.data && <OrganizationIcon org={currentOrg.data} />}
          <Text
            weight="semibold"
            className="flex-1 overflow-hidden overflow-ellipsis text-left"
          >
            {currentOrg.data?.display_name}
          </Text>
        </div>
        <ChevronDownIcon className="h-4 w-4 flex-none" />
      </PopoverTrigger>

      <PopoverContent
        className={cn(
          'relative max-h-[400px] w-[250px] p-0',
          'flex flex-col text-xs'
        )}
      >
        <div className="flex-1 overflow-y-auto">
          {organizations?.map((org) => {
            const orgTenants = tenants?.filter(
              (t) => t.organization_id === org.id
            );

            return (
              <OrganizationButton
                key={org.id}
                org={org}
                isSelected={org.id === currentOrg.data?.id}
                isSAMLEmptyState={isSAMLEmptyState}
                onSelect={() => {
                  setOpen(false);
                  // If org has workspaces, navigate to the first workspace settings
                  // Otherwise, navigate to organization settings
                  if (orgTenants && orgTenants.length > 0) {
                    navigate(
                      `${appOrganizationPath}/${orgTenants[0].id}/settings`
                    );
                  } else {
                    // For organizations with no workspaces, navigate to organization settings
                    navigate(
                      `/${appOrganizationPath}/current/${appSettingsPath}`
                    );
                  }
                }}
              />
            );
          })}
        </div>
        {authorize('organization:manage') && (
          <div className="border-t-[1px] border-t-secondary bg-background">
            {!orgCreationDisabled && (
              <CreateOrganizationModal
                onSuccess={(tenant) => {
                  if (tenant != null) {
                    navigate(
                      `/${appOrganizationPath}/${tenant.id}/${appSettingsPath}`
                    );
                  }
                }}
              >
                {({ onClick }) => (
                  <Button
                    onClick={onClick}
                    variant="plain"
                    leftDecorator={PlusIcon}
                    color="secondary"
                    size="md"
                    className="w-full justify-start pr-1"
                  >
                    Create organization
                  </Button>
                )}
              </CreateOrganizationModal>
            )}
            {isSelfHostedSSO && <DefaultOrgModal />}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
